import { useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Chip,
  Grid,
  LinearProgress,
} from "@mui/material";
import {
  GpsFixed,
  LocalShipping,
  CheckCircle,
  LocationOn,
  Schedule,
  ShoppingBag,
  Route,
} from "@mui/icons-material";
import { tracking } from "../../api/ups";
import Notification from "../../Components/Alert/AlertDialog";

const Tracking = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [alertMsg, setAlertMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [showTracking, setShowTracking] = useState(false);
  const [data, setData] = useState({});
  const [shpData, setShpData] = useState({});
  const [loading, setLoading] = useState(false);

  const handleTracking = async () => {
    setShowTracking(false);

    if (trackingNumber === "") {
      setAlertMessage("Le numéro de suivi ne peut pas être vide !!");
      setOpen(true);
      setAlertType("error");
      return;
    }

    closeNotification();
    setAlertMessage("La demande est en cours de traitement !!");
    setOpen(true);
    setAlertType("info");
    setLoading(true);

    try {
      const resp = await tracking(trackingNumber);
      closeNotification();
      setLoading(false);

      if (resp?.response?.trackResponse) {
        setData(resp?.response);
        setShpData(
          resp?.response?.trackResponse?.shipment[0]?.package[0]
            ?.packageAddress,
        );
        setShowTracking(true);
        setAlertMessage("Suivi récupéré avec succès !");
        setOpen(true);
        setAlertType("success");
      } else if (resp?.response?.response?.errors?.[0]?.message) {
        setAlertMessage(resp?.response?.response?.errors[0]?.message);
        setOpen(true);
        setAlertType("error");
      } else {
        setAlertMessage("Aucune donnée de suivi trouvée pour ce numéro");
        setOpen(true);
        setAlertType("warning");
      }
    } catch (error) {
      setLoading(false);
      setAlertMessage(
        "Erreur lors de la récupération du suivi. Veuillez réessayer.",
      );
      setOpen(true);
      setAlertType("error");
      console.error("Tracking error:", error);
    }
  };

  function closeNotification() {
    setAlertMessage("");
    setOpen(false);
    setAlertType("");
  }

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("delivered") || statusLower.includes("livré")) {
      return "success";
    } else if (
      statusLower.includes("transit") ||
      statusLower.includes("en cours")
    ) {
      return "primary";
    } else if (
      statusLower.includes("exception") ||
      statusLower.includes("retard")
    ) {
      return "warning";
    }
    return "default";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      let date;

      // Handle YYYYMMDD format (e.g. 20250929)
      if (/^\d{8}$/.test(dateString)) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateString);
      }

      return date.toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const renderTrackingDetails = () => {
    if (!data?.trackResponse?.shipment?.[0]?.package?.[0]) return null;

    const pkg = data.trackResponse.shipment[0].package[0];
    const currentStatus =
      pkg?.currentStatus?.simplifiedTextDescription ||
      pkg?.currentStatus?.description ||
      "Statut inconnu";
    const activities = pkg?.activity || [];
    const deliveryDate = pkg?.deliveryDate?.[0];
    const weight = pkg?.packageWeight;
    const service = pkg?.service?.description;

    return (
      <Box sx={{ mt: 4 }}>
        {/* Status Card */}
        <Card
          elevation={3}
          sx={{
            mb: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <LocalShipping sx={{ fontSize: 40 }} />
              <Box flex={1}>
                <Typography variant="h5" fontWeight="bold">
                  {currentStatus}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Numéro de suivi: {trackingNumber}
                </Typography>
              </Box>
              <Chip
                label={getStatusColor(currentStatus).toUpperCase()}
                color={getStatusColor(currentStatus)}
                sx={{ fontWeight: "bold" }}
              />
            </Box>
            {deliveryDate && (
              <Box display="flex" alignItems="center" gap={1}>
                <Schedule />
                <Typography variant="body1">
                  Livraison estimée: {formatDate(deliveryDate.date)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Shipping Details Grid */}
        <Grid container spacing={3} mb={3}>
          {/* Origin */}
          <Grid item xs={12} md={5}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <LocationOn color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Origine
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {shpData[0]?.address?.city || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {shpData[0]?.address?.stateProvince || ""}{" "}
                  {shpData[0]?.address?.postalCode || ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {shpData[0]?.address?.country || "N/A"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Arrow */}
          <Grid
            item
            xs={12}
            md={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Route sx={{ fontSize: 40, color: "primary.main" }} />
          </Grid>

          {/* Destination */}
          <Grid item xs={12} md={5}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <LocationOn color="secondary" />
                  <Typography variant="h6" fontWeight="bold">
                    Destination
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {shpData[1]?.address?.city || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {shpData[1]?.address?.stateProvince || ""}{" "}
                  {shpData[1]?.address?.postalCode || ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {shpData[1]?.address?.country || "N/A"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ShoppingBag Details */}
        {(weight || service) && (
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ShoppingBag color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Détails du colis
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {weight && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Poids
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {weight.weight} {weight.unitOfMeasurement}
                    </Typography>
                  </Grid>
                )}
                {service && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Service
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {service}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Activity Timeline */}
        {activities.length > 0 && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Historique de suivi
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stepper orientation="vertical">
                {activities.map((activity, index) => (
                  <Step key={index} active={true} completed={index > 0}>
                    <StepLabel
                      StepIconComponent={() => (
                        <CheckCircle
                          sx={{
                            color:
                              index === 0 ? "primary.main" : "success.main",
                            fontSize: 28,
                          }}
                        />
                      )}
                    >
                      <Typography variant="body1" fontWeight="medium">
                        {activity.status?.description ||
                          "Mise à jour du statut"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(activity.date)} {activity.time || ""}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {activity.location && (
                        <Typography variant="body2" color="text.secondary">
                          📍{" "}
                          {[
                            activity.location.address?.city,
                            activity.location.address?.stateProvince,
                            activity.location.address?.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: 3 }}>
      <Box flexGrow={1}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
          Suivi de colis
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Saisissez votre identifiant de suivi pour suivre vos articles
        </Typography>

        <Box sx={{ maxWidth: 600 }}>
          <TextField
            onChange={(e) => setTrackingNumber(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleTracking()}
            fullWidth
            label="Votre identifiant de suivi"
            variant="outlined"
            color="primary"
            disabled={loading}
            value={trackingNumber}
            placeholder="Ex: 1Z999AA10123456784"
          />
          <Button
            onClick={handleTracking}
            fullWidth
            variant="contained"
            color="warning"
            sx={{ marginTop: 2, py: 1.5 }}
            size="large"
            startIcon={<GpsFixed />}
            disabled={loading}
          >
            {loading ? "Traitement en cours..." : "Suivre votre colis"}
          </Button>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      </Box>

      {showTracking && renderTrackingDetails()}

      {open && (
        <Notification
          open={open}
          message={alertMsg}
          severity={alertType}
          horizontal={"right"}
          handleClose={closeNotification}
        />
      )}
    </Box>
  );
};

export default Tracking;
